import {
    FetchState,
    HealthcareExpenseState,
    HealthcareExpenseForecastRequest,
    HsaForecastResponse,
    HealthcareExpenseCondition,
    UserFeedbackEventAction
} from "@/shared/types";

import * as healthcareExpenseService from "@/shared/api/healthcareExpenseService";
import * as storeHelper from "@/shared/storeHelper";
import { errorIsStatus } from "@/shared/errorHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";

export const initialState: HealthcareExpenseState = {
    forecastRequestFetchState: FetchState.Uninitialized,
    forecastRequest: null,
    hsaForecastFetchState: FetchState.Uninitialized,
    hsaForecastResponse: null,
    conditionsFetchState: FetchState.Uninitialized,
    conditions: [],
};
export const useContributionPlannerStore = defineStore("contributionPlanner", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isForecastRequestLoaded: state => state.forecastRequestFetchState === FetchState.Loaded,
        isHsaForecastLoaded: state => state.hsaForecastFetchState === FetchState.Loaded,
        hsaForecast: state => state.hsaForecastResponse,
        familyMembers: state => state.forecastRequest && state.forecastRequest.familyMembers
            ? state.forecastRequest.familyMembers : [],
        isConditionsLoaded: state => state.conditionsFetchState === FetchState.Loaded,
        hsaForecastLoading: state => state.hsaForecastFetchState === FetchState.Loading,
        hsaForecastHasData: state => state.hsaForecastFetchState === FetchState.Loaded
            && state.hsaForecastResponse.hsaForecasts.length > 0
    },
    actions: {
        setForecastRequestFetchState (newState: FetchState) {
            this.forecastRequestFetchState = newState;
        },
        setForecastRequest (forecastRequest: HealthcareExpenseForecastRequest): void {
            this.forecastRequest = forecastRequest;
        },
        setHsaForecastFetchState (newState: FetchState) {
            this.hsaForecastFetchState = newState;
        },
        setHsaForecastResponse (hsaForecastResponse: HsaForecastResponse): void {
            hsaForecastResponse.hsaForecasts.sort((a, b) => (a.year > b.year) ? 1 : -1);
            this.hsaForecastResponse = hsaForecastResponse;
        },
        setConditionsFetchState (newState: FetchState) {
            this.conditionsFetchState = newState;
        },
        setConditions (conditions: HealthcareExpenseCondition[]): void {
            this.conditions = conditions;
        },
        loadConditions (): Promise<any> {
            if (this.conditionsFetchState === FetchState.Loaded || this.conditionsFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setConditionsFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return healthcareExpenseService.getConditions().then(response => {
                this.setConditions(response.data);
                this.setConditionsFetchState(FetchState.Loaded);
            }).catch(err => {
                this.setConditionsFetchState(FetchState.Loaded);
                if (errorIsStatus(err, 503)) {
                    notifierStore.setDanger("The service is unavailable. Please try again later.");
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        },
        loadForecastRequest (): Promise<any> {
            this.setForecastRequestFetchState(FetchState.Loading);
            return healthcareExpenseService.getForecastRequest().then(response => {
                this.setForecastRequest(response.data);
                this.setForecastRequestFetchState(FetchState.Loaded);
            }).catch(err => {
                if (errorIsStatus(err, 404)) {
                    this.setForecastRequestFetchState(FetchState.Loaded);
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        },
        loadHsaForecast (): Promise<any> {
            if ((this.hsaForecastFetchState === FetchState.Loaded && this.hsaForecastResponse.hsaForecasts.length > 0)
                || this.hsaForecastFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setHsaForecastFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return healthcareExpenseService.getHsaForecast().then(response => {
                this.setHsaForecastResponse(response.data);
                this.setHsaForecastFetchState(FetchState.Loaded);
            }).catch(err => {
                if (errorIsStatus(err, 503)) {
                    notifierStore.setDanger("The service is unavailable. Please try again later.");
                    this.setHsaForecastFetchState(FetchState.Uninitialized);
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        },
        updateHsaForecast (request): Promise<any> {
            const fixRequest = { ...request };

            if (fixRequest.zipCode && fixRequest.zipCode.length > 5) {
                fixRequest.zipCode = fixRequest.zipCode.substr(0, 5);
            }

            this.setHsaForecastFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            const userFeedbackStore = useUserFeedbackStore();
            return healthcareExpenseService.updateHsaForecast(fixRequest).then(response => {
                this.setHsaForecastResponse(response.data);
                this.loadForecastRequest();
                this.setHsaForecastFetchState(FetchState.Loaded);
                userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.ContributionPlanner);
            }).catch(err => {
                if (errorIsStatus(err, 503)) {
                    notifierStore.setDanger("The service is unavailable. Please try again later.");
                    this.setHsaForecastFetchState(FetchState.Uninitialized);
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        }
    }
});